import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useMisEmpresas } from '../hooks/useMisDatos';
import { formatDate } from '../utils/format';
import { formatCuil } from '../utils/identity';

function Campos({ filas }: { filas: [string, string | null | undefined][] }) {
  return (
    <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'minmax(110px, max-content) 1fr', gap: 1, columnGap: 3 }}>
      {filas.map(([k, v]) => (
        <Box key={k} sx={{ display: 'contents' }}>
          <Typography component="dt" color="text.secondary">
            {k}
          </Typography>
          <Typography component="dd" fontWeight={600} sx={{ m: 0, overflowWrap: 'anywhere' }}>
            {v || '-'}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

const ESTADO_CIVIL: Record<string, string> = { soltero: 'Soltero/a', casado: 'Casado/a', divorciado: 'Divorciado/a', viudo: 'Viudo/a', conviviente: 'Conviviente' };
const SEXO: Record<string, string> = { M: 'Masculino', F: 'Femenino', X: 'No binario' };

export default function MisDatos() {
  const { ficha } = useAuth();
  const { data: empresas, isLoading, error } = useMisEmpresas();
  if (!ficha) return null;

  return (
    <Stack spacing={3}>
      <Typography variant="h5" fontWeight={700}>
        Mis datos
      </Typography>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Datos personales
            </Typography>
            <Campos
              filas={[
                ['Apellido y nombre', `${ficha.apellido}, ${ficha.nombre}`],
                ['CUIL', formatCuil(ficha.cuil)],
                ['Documento', `${ficha.tipo_documento ?? ''} ${ficha.numero_documento}`.trim()],
                ['Nacimiento', formatDate(ficha.fecha_nacimiento)],
                ['Sexo', ficha.sexo ? SEXO[ficha.sexo] ?? ficha.sexo : null],
                ['Estado civil', ficha.estado_civil ? ESTADO_CIVIL[ficha.estado_civil] ?? ficha.estado_civil : null],
              ]}
            />
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Contacto
            </Typography>
            <Campos
              filas={[
                ['Teléfono', ficha.telefono],
                ['Correo', ficha.email],
                ['Domicilio', ficha.domicilio],
                ['Localidad', [ficha.localidad, ficha.provincia].filter(Boolean).join(', ')],
              ]}
            />
          </CardContent>
        </Card>
      </Box>
      <Alert
        severity="info"
        action={
          <Button color="inherit" size="small" component={RouterLink} to="/tramites/nuevo?tipo=correccion_datos">
            Pedir corrección
          </Button>
        }
      >
        ¿Algún dato está mal? Pedí la corrección y el gremio la revisa.
      </Alert>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Mis empresas
          </Typography>
          {isLoading && <CircularProgress size={24} />}
          {error && <Alert severity="error">No pudimos cargar tus empresas.</Alert>}
          {!isLoading && !error && (empresas ?? []).length === 0 && <Typography color="text.secondary">No figurás en ninguna empresa.</Typography>}
          <Stack spacing={2} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
            {(empresas ?? []).map((e) => (
              <Stack key={e.relacion_id} direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                <Box>
                  <Typography fontWeight={600}>{e.razon_social}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    CUIT {formatCuil(e.cuit)}
                    {e.categoria ? ` · ${e.categoria}` : ''}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">
                    {formatDate(e.fecha_alta)} → {e.fecha_baja ? formatDate(e.fecha_baja) : 'hoy'}
                  </Typography>
                  <Chip size="small" label={e.estado === 'activa' ? 'Activa' : 'Baja'} color={e.estado === 'activa' ? 'success' : 'default'} />
                </Stack>
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
